/**
 * 路由工具函數
 * 處理國際化路由的語言前綴和導航
 */

// 支持的語言列表
export const supportedLocales = ['en', 'zh', 'ja']

/**
 * 根據 URL 語言代碼和瀏覽器設置獲取對應的 i18n 語言代碼
 * @param {string} urlLocale - URL 語言代碼 (en, zh, ja)
 * @returns {string} i18n 語言代碼
 */
export function getI18nLocaleFromUrlLocale(urlLocale) {
  if (urlLocale === 'en') {
    return 'en-US'
  }
  
  if (urlLocale === 'ja') {
    return 'ja-JP'
  }
  
  if (urlLocale === 'zh') {
    // 檢查 localStorage 中保存的語言偏好
    const saved = localStorage.getItem('locale')
    if (saved && (saved === 'zh-TW' || saved === 'zh-CN')) {
      return saved
    }
    
    // 根據瀏覽器語言自動選擇
    const browserLang = navigator.language || navigator.userLanguage
    if (browserLang.startsWith('zh')) {
      // 簡體中文：zh-CN, zh-Hans, zh-SG 等
      if (browserLang.includes('CN') || browserLang.includes('Hans') || browserLang.includes('SG')) {
        return 'zh-CN'
      }
      // 繁體中文：zh-TW, zh-Hant, zh-HK 等
      return 'zh-TW'
    }
    
    // 默認使用繁體中文
    return 'zh-TW'
  }
  
  // 默認返回英文
  return 'en-US'
}

// 注意：使用 getI18nLocaleFromUrlLocale() 函數來獲取對應的 i18n locale

// i18n 語言代碼 -> URL 語言代碼（反向映射）
export const i18nToLocaleMap = {
  'en-US': 'en',
  'en': 'en',
  'zh-TW': 'zh',
  'zh-CN': 'zh',
  'zh': 'zh',
  'ja-JP': 'ja',
  'ja': 'ja'
}

/**
 * 獲取當前路由的語言代碼
 * @param {string} path - 路由路徑
 * @returns {string|null} 語言代碼或 null
 */
export function getLocaleFromPath(path) {
  const segments = path.split('/').filter(Boolean)
  if (segments.length > 0 && supportedLocales.includes(segments[0])) {
    return segments[0]
  }
  return null
}

/**
 * 從路徑中移除語言前綴
 * @param {string} path - 路由路徑
 * @returns {string} 移除語言前綴後的路徑
 */
export function removeLocaleFromPath(path) {
  const segments = path.split('/').filter(Boolean)
  if (segments.length > 0 && supportedLocales.includes(segments[0])) {
    return '/' + segments.slice(1).join('/')
  }
  return path || '/'
}

/**
 * 為路徑添加語言前綴
 * @param {string} path - 路由路徑
 * @param {string} locale - 語言代碼
 * @returns {string} 添加語言前綴後的路徑
 */
export function addLocaleToPath(path, locale) {
  // 確保 path 以 / 開頭
  const cleanPath = path.startsWith('/') ? path : '/' + path
  
  // 如果路徑已經是 /，直接返回 /locale
  if (cleanPath === '/') {
    return `/${locale}`
  }
  
  // 如果路徑已經有語言前綴，先移除
  const pathWithoutLocale = removeLocaleFromPath(cleanPath)
  
  // 添加新的語言前綴
  if (pathWithoutLocale === '/') {
    return `/${locale}`
  }
  
  return `/${locale}${pathWithoutLocale}`
}

/**
 * 檢測瀏覽器語言並返回對應的 URL 語言代碼
 * @returns {string} 語言代碼（默認 'en'）
 */
export function detectBrowserLocale() {
  // 先檢查 localStorage 中保存的語言
  const saved = localStorage.getItem('locale')
  if (saved) {
    const urlLocale = i18nToLocaleMap[saved]
    if (urlLocale) {
      return urlLocale
    }
  }
  
  // 檢測瀏覽器語言
  const browserLang = navigator.language || navigator.userLanguage
  
  // 簡化語言代碼（例如 'en-US' -> 'en'）
  const langCode = browserLang.split('-')[0].toLowerCase()
  
  // 特殊處理中文
  if (langCode === 'zh') {
    // 如果瀏覽器語言是繁體中文相關，返回 'zh'
    if (browserLang.includes('TW') || browserLang.includes('HK') || browserLang.includes('Hant')) {
      return 'zh'
    }
    // 簡體中文也返回 'zh'（由 i18n 內部處理簡繁體）
    return 'zh'
  }
  
  // 檢查是否支持
  if (supportedLocales.includes(langCode)) {
    return langCode
  }
  
  // 默認返回英文
  return 'en'
}

/**
 * 檢查路徑是否為管理後台路由
 * @param {string} path - 路由路徑
 * @returns {boolean}
 */
export function isAdminPath(path) {
  return path.startsWith('/admin')
}

/**
 * 檢查路徑是否需要語言前綴
 * @param {string} path - 路由路徑
 * @returns {boolean}
 */
export function needsLocalePrefix(path) {
  // 管理後台路由不需要語言前綴
  if (isAdminPath(path)) {
    return false
  }
  
  // 已經有語言前綴的路徑
  const locale = getLocaleFromPath(path)
  if (locale) {
    return false
  }
  
  return true
}

